import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  FileDown, 
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Camera,
  X,
  Eye,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ConstructionCanvas } from '@/components/canvas/ConstructionCanvas';
import { PdfPreviewModal } from '@/components/pdf/PdfPreviewModal';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GeneradoresPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject } = useApp();
  const { user } = useAuth();
  
  // Check if came from Admin Dashboard
  const fromAdmin = location.state?.fromAdmin === true;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [generadoresData, setGeneradoresData] = useState([]);
  const [currentWeekInfo, setCurrentWeekInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedConcepts, setExpandedConcepts] = useState({});
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [generatingPhotoPdf, setGeneratingPhotoPdf] = useState(null); // capture_id being generated
  const [pdfPreview, setPdfPreview] = useState(null);  // { path, filename } for preview modal
  // Borrado (solo Admin): { type: 'capture' | 'figure', id, label, figuresCount }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const askDeleteCapture = (capture, e) => {
    e.stopPropagation();
    const photoNum = capture.photo_number ? String(capture.photo_number).padStart(3, '0') : '---';
    setDeleteTarget({ type: 'capture', id: capture.id, label: `Foto ${photoNum}`, figuresCount: capture.figures.length });
  };

  const askDeleteFigure = (fig) => {
    setDeleteTarget({ type: 'figure', id: fig.id, label: `Figura ${String(fig.figure_number).padStart(6, '0')}`, figuresCount: 0 });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'capture') {
        await axios.delete(`${API}/captures/${deleteTarget.id}`);
        if (selectedPhoto?.capture?.id === deleteTarget.id) setSelectedPhoto(null);
        toast.success(`${deleteTarget.label} eliminada con sus figuras`);
      } else {
        await axios.delete(`${API}/figures/${deleteTarget.id}`);
        // Actualizar el visor abierto sin cerrarlo
        setSelectedPhoto((prev) => {
          if (!prev) return prev;
          const figures = prev.capture.figures.filter((f) => f.id !== deleteTarget.id);
          const subtotal = figures.reduce((acc, f) => acc + (f.tipo === 'deduccion' ? -(f.cantidad || 0) : (f.cantidad || 0)), 0);
          return { ...prev, capture: { ...prev.capture, figures, subtotal } };
        });
        toast.success(`${deleteTarget.label} eliminada`);
      }
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!selectedProject) {
      console.error('GENERADORES: No selectedProject!');
      return;
    }
    
    setLoading(true);
    console.log('=== GENERADORES FETCH ===');
    console.log('selectedProject:', selectedProject);
    console.log('selectedProject.id:', selectedProject.id);
    console.log('API URL:', `${API}/generadores/${selectedProject.id}`);
    
    try {
      const response = await axios.get(`${API}/generadores/${selectedProject.id}`);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      
      // Handle new response format with data and current_week
      const responseData = response.data;
      
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        // New format: { data: [...], current_week: {...} }
        setGeneradoresData(responseData.data);
        setCurrentWeekInfo(responseData.current_week);
      } else if (Array.isArray(responseData)) {
        // Old format: direct array (fallback)
        setGeneradoresData(responseData);
        setCurrentWeekInfo(null);
      } else {
        console.error('Invalid response format:', responseData);
        toast.error('Formato de respuesta inválido');
        setGeneradoresData([]);
      }
    } catch (error) {
      console.error('GENERADORES ERROR:', error);
      console.error('Error response:', error.response);
      toast.error(`Error al cargar datos: ${error.message}`);
      setGeneradoresData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) {
      navigate('/contratos');
      return;
    }
    fetchData();
  }, [selectedProject, navigate, fetchData]);

  const handleBack = () => {
    if (fromAdmin && isAdmin) {
      navigate('/admin');
    } else {
      navigate('/contrato');
    }
  };

  const toggleConcept = (conceptId) => {
    setExpandedConcepts(prev => ({
      ...prev,
      [conceptId]: !prev[conceptId]
    }));
  };

  const toggleWeek = (conceptId, weekNum) => {
    const key = `${conceptId}-${weekNum}`;
    setExpandedWeeks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleGeneratePdf = async (conceptId, weekNumber) => {
    if (generatingPdf) return;
    
    const weekKey = `${conceptId}-${weekNumber}`;
    setGeneratingPdf(weekKey);
    
    try {
      const response = await axios.post(`${API}/pdf/generate-week`, {
        project_id: selectedProject.id,
        concept_id: conceptId,
        week_number: weekNumber
      });
      
      toast.success(`PDF generado con ${response.data.photo_count} fotos`);
      
      // Open PDF preview modal
      setPdfPreview({
        path: response.data.pdf_path,
        filename: response.data.pdf_filename
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(error.response?.data?.detail || 'Error al generar PDF');
    } finally {
      setGeneratingPdf(null);
    }
  };

  const handleClosePdfPreview = () => {
    setPdfPreview(null);
  };

  const handleGeneratePhotoPdf = async (captureId, conceptId, e) => {
    // Prevent the click from opening the photo viewer
    e.stopPropagation();
    
    if (generatingPhotoPdf) return;
    
    setGeneratingPhotoPdf(captureId);
    
    try {
      const response = await axios.post(`${API}/pdf/generate-photo`, {
        capture_id: captureId,
        concept_id: conceptId
      });
      
      toast.success(`PDF de foto generado con ${response.data.figure_count} figuras`);
      
      // Open PDF preview modal
      setPdfPreview({
        path: response.data.pdf_path,
        filename: response.data.pdf_filename
      });
      
    } catch (error) {
      console.error('Error generating photo PDF:', error);
      toast.error(error.response?.data?.detail || 'Error al generar PDF de foto');
    } finally {
      setGeneratingPhotoPdf(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Format date and time for photo display: "22 mar 2026 · 10:32am"
  const formatPhotoDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate();
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${day} ${month} ${year} · ${hours}:${minutes}${ampm}`;
  };

  const formatDimensions = (fig, unit) => {
    const cantidad = fig.cantidad || 0;
    const cantidadFmt = cantidad < 0.01 && cantidad > 0 
      ? cantidad.toFixed(4) 
      : cantidad.toFixed(2);
    
    if (fig.largo && fig.alto && fig.profundidad) {
      return `L:${parseFloat(fig.largo).toFixed(2)}×A:${parseFloat(fig.alto).toFixed(2)}×Prof:${parseFloat(fig.profundidad).toFixed(2)}=${cantidadFmt}${unit}`;
    } else if (fig.largo && fig.alto) {
      return `L:${parseFloat(fig.largo).toFixed(2)}×A:${parseFloat(fig.alto).toFixed(2)}=${cantidadFmt}${unit}`;
    } else if (fig.longitud) {
      return `L:${parseFloat(fig.longitud).toFixed(2)}=${cantidadFmt}${unit}`;
    } else if (fig.piezas !== undefined && fig.piezas !== null) {
      return `${fig.piezas} pines`;
    } else if (fig.diametro && fig.profundidad) {
      return `D:${parseFloat(fig.diametro).toFixed(2)}×Prof:${parseFloat(fig.profundidad).toFixed(2)}=${cantidadFmt}${unit}`;
    } else if (fig.diametro) {
      return `D:${parseFloat(fig.diametro).toFixed(2)}=${cantidadFmt}${unit}`;
    }
    return `${cantidadFmt}${unit}`;
  };

  const isContratista = user?.role === 'contractor';
  const isResidente = user?.role === 'inspector';

  if (!selectedProject) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#060C18]" data-testid="generadores-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size={fromAdmin && isAdmin ? "default" : "icon"}
              onClick={handleBack}
              className="text-[#E2EAF4] hover:bg-[#1E3550]"
              data-testid="back-to-contrato"
            >
              <ArrowLeft className="w-5 h-5" />
              {fromAdmin && isAdmin && <span className="ml-2">Admin Dashboard</span>}
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-[#FF6B00]">GENERADORES</h1>
              <p className="text-[#5A7A9A] text-sm">{selectedProject.name}</p>
              {currentWeekInfo && (
                <p className="text-xs text-[#5A7A9A] mt-1">
                  Semana activa: <span className="text-[#FF6B00]">{currentWeekInfo.name}</span>
                  {currentWeekInfo.date_range && <span className="text-[#5A7A9A]"> · {currentWeekInfo.date_range}</span>}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchData}
              className="text-[#E2EAF4] hover:bg-[#1E3550]"
              disabled={loading}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-[#FF6B00] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : generadoresData.length === 0 ? (
          <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-8 text-center">
            <Camera className="w-16 h-16 text-[#1E3550] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#E2EAF4] mb-2">Sin datos</h3>
            <p className="text-[#5A7A9A] mb-4">No se encontraron conceptos para este proyecto</p>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-[#FF6B00] text-[#FF6B00] hover:bg-[#FF6B00]/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {generadoresData.map((item) => {
              const concept = item.concept;
              const weeks = item.weeks || [];
              const isExpanded = expandedConcepts[concept.id];
              
              return (
                <div 
                  key={concept.id} 
                  className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-hidden"
                  data-testid={`concept-${concept.id}`}
                >
                  {/* Concept Header - Clickable */}
                  <button
                    onClick={() => toggleConcept(concept.id)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#1E3550]/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-[#5A7A9A]" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-[#5A7A9A]" />
                      )}
                      <div className="text-left">
                        <span className="font-bold text-[#FF6B00]">{concept.clave}</span>
                        <span className="ml-2 text-[#E2EAF4]">{concept.descripcion}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs bg-[#0A1422] border border-[#1E3550] px-2 py-1 rounded font-medium text-[#00CFFF]">
                        {concept.unidad}
                      </span>
                      <span className="font-mono font-semibold text-[#0066FF]">
                        {item.acumulado_total.toFixed(2)} {concept.unidad}
                      </span>
                    </div>
                  </button>

                  {/* Expanded Content - Weeks */}
                  {isExpanded && (
                    <div className="border-t border-[#1E3550] bg-[#0A1422]">
                      {weeks.length === 0 ? (
                        <div className="px-6 py-4 text-center text-[#5A7A9A] text-sm">
                          Sin capturas para este concepto
                        </div>
                      ) : (
                        weeks.map((week) => {
                          const weekKey = `${concept.id}-${week.week_number}`;
                          const isWeekExpanded = expandedWeeks[weekKey];
                          const isClosed = week.status === 'closed';
                          const isCurrent = week.is_current;
                          
                          return (
                            <div key={week.week_number} className="border-b border-[#1E3550] last:border-b-0">
                              {/* Week Header */}
                              <button
                                onClick={() => toggleWeek(concept.id, week.week_number)}
                                className={`w-full px-6 py-3 flex items-center justify-between hover:bg-[#1E3550]/50 transition-colors ${
                                  isClosed ? 'bg-[#22C55E]/10' : 'bg-[#FF6B00]/10'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  {isWeekExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-[#5A7A9A]" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-[#5A7A9A]" />
                                  )}
                                  <div className="flex flex-col items-start gap-0.5">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-[#E2EAF4]">
                                        {week.name}
                                      </span>
                                      {isClosed ? (
                                        <span className="text-[#22C55E] text-xs font-medium px-2 py-0.5 bg-[#22C55E]/20 rounded">CERRADA ✅</span>
                                      ) : isCurrent ? (
                                        <span className="text-[#FF6B00] text-xs font-medium px-2 py-0.5 bg-[#FF6B00]/20 rounded">EN CURSO 🔄</span>
                                      ) : (
                                        <span className="text-[#5A7A9A] text-xs font-medium px-2 py-0.5 bg-[#5A7A9A]/20 rounded">ABIERTA</span>
                                      )}
                                    </div>
                                    {week.date_range && (
                                      <span className="text-xs text-[#5A7A9A]">{week.date_range}</span>
                                    )}
                                  </div>
                                </div>
                                <span className="font-mono text-sm font-semibold text-[#E2EAF4]">
                                  {week.total.toFixed(2)} {concept.unidad}
                                </span>
                              </button>

                              {/* Week Content */}
                              {isWeekExpanded && (
                                <div className="bg-[#0D1520] px-6 py-3">
                                  {/* Captures/Photos with thumbnails */}
                                  {week.captures.map((capture) => {
                                    const photoNum = capture.photo_number 
                                      ? String(capture.photo_number).padStart(3, '0') 
                                      : '---';
                                    const photoDateTime = formatPhotoDateTime(capture.created_at);
                                    const inspectorName = capture.inspector_name || '';
                                    const isGeneratingThisPhoto = generatingPhotoPdf === capture.id;
                                    
                                    return (
                                    <div key={capture.id} className="mb-3 pl-2 border-l-2 border-[#1E3550]">
                                      <div className="flex items-start gap-2">
                                        <button
                                          onClick={() => setSelectedPhoto({ capture, concept, week })}
                                          className="flex items-start gap-3 flex-1 text-left hover:bg-[#1E3550]/50 rounded p-1"
                                        >
                                          {/* Thumbnail */}
                                          <CaptureThumb captureId={capture.id} />
                                          
                                          <div className="flex-1 min-w-0">
                                            {/* Photo number, timestamp, and inspector name */}
                                            <div className="text-xs text-[#5A7A9A] mb-1" data-testid={`photo-info-${capture.id}`}>
                                              <span className="font-semibold text-[#0066FF]">Foto {photoNum}</span>
                                              {photoDateTime && <span className="mx-1">·</span>}
                                              <span>{photoDateTime}</span>
                                              {inspectorName && <span className="mx-1">·</span>}
                                              {inspectorName && <span className="text-[#00CFFF]">{inspectorName}</span>}
                                            </div>
                                            
                                            {/* Figure list for this capture */}
                                            <div className="space-y-0.5">
                                              {capture.figures.slice(0, 3).map((fig) => (
                                                <div 
                                                  key={fig.id} 
                                                  className={`text-xs font-mono truncate ${
                                                    fig.tipo === 'deduccion' ? 'text-red-500' : 'text-[#E2EAF4]'
                                                  }`}
                                                >
                                                  {fig.tipo === 'deduccion' ? '−' : '+'} {formatDimensions(fig, concept.unidad)}
                                                </div>
                                              ))}
                                              {capture.figures.length > 3 && (
                                                <div className="text-xs text-[#5A7A9A]">
                                                  +{capture.figures.length - 3} más...
                                                </div>
                                              )}
                                            </div>
                                            <div className="text-xs font-semibold text-[#E2EAF4] mt-1">
                                              Subtotal: {capture.subtotal.toFixed(2)}{concept.unidad}
                                            </div>
                                          </div>
                                        </button>
                                        
                                        {/* Preview PDF Button - Only show if capture has figures */}
                                        {capture.figures.length > 0 && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={(e) => handleGeneratePhotoPdf(capture.id, concept.id, e)}
                                            disabled={isGeneratingThisPhoto}
                                            className="h-8 px-2 text-[#5A7A9A] hover:text-[#00CFFF] hover:bg-[#1E3550] flex-shrink-0"
                                            data-testid={`btn-preview-photo-${capture.id}`}
                                          >
                                            {isGeneratingThisPhoto ? (
                                              <RefreshCw className="w-4 h-4 animate-spin" />
                                            ) : (
                                              <>
                                                <Eye className="w-4 h-4 mr-1" />
                                                <span className="text-xs">PDF</span>
                                              </>
                                            )}
                                          </Button>
                                        )}
                                        {/* Eliminar foto (solo Admin) */}
                                        {isAdmin && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={(e) => askDeleteCapture(capture, e)}
                                            className="h-8 px-2 text-[#5A7A9A] hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
                                            title="Eliminar foto y sus figuras"
                                            data-testid={`btn-delete-capture-${capture.id}`}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    );
                                  })}

                                  {/* Week Total */}
                                  <div className="mt-3 pt-2 border-t border-[#1E3550] font-semibold text-sm text-[#E2EAF4] pl-6">
                                    TOTAL {week.name}: {week.total.toFixed(2)}{concept.unidad}
                                  </div>

                                  {/* PDF Generation Section - Both Residente and Contratista, all weeks (TESTING) */}
                                  {(isContratista || isResidente) && (
                                    <div className="mt-6 px-4">
                                      <Button
                                        onClick={() => handleGeneratePdf(concept.id, week.week_number)}
                                        disabled={generatingPdf === weekKey}
                                        className="w-full h-14 bg-[#0066FF] hover:bg-[#0052CC] text-white font-bold text-base rounded-xl"
                                        data-testid={`btn-generate-pdf-${weekKey}`}
                                      >
                                        {generatingPdf === weekKey ? (
                                          <>
                                            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                            Creando PDF...
                                          </>
                                        ) : (
                                          <>
                                            <FileDown className="w-5 h-5 mr-2" />
                                            CREAR PDF
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}

                      {/* Accumulated Total */}
                      {weeks.length > 0 && (
                        <div className="px-6 py-3 bg-[#0066FF] text-white font-bold text-sm">
                          ACUMULADO TOTAL: {item.acumulado_total.toFixed(2)} {concept.unidad}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <PhotoViewerModal
          capture={selectedPhoto.capture}
          concept={selectedPhoto.concept}
          week={selectedPhoto.week}
          onClose={() => setSelectedPhoto(null)}
          isAdmin={isAdmin}
          onDeleteFigure={askDeleteFigure}
        />
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        loading={deleting}
        testId="confirm-delete-generadores"
        title={`Eliminar ${deleteTarget?.label || ''}`}
        description={
          deleteTarget?.type === 'capture'
            ? `Se eliminará la foto y sus ${deleteTarget.figuresCount} figura(s) del generador.`
            : 'Se eliminará la figura y su cantidad dejará de contar en el generador.'
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      {/* PDF Preview Modal */}
      {pdfPreview && (
        <PdfPreviewModal
          pdfPath={pdfPreview.path}
          pdfFilename={pdfPreview.filename}
          onClose={handleClosePdfPreview}
        />
      )}
    </div>
  );
};

// Thumbnail component for capture list - loads photo on demand
const CaptureThumb = ({ captureId }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!captureId) return;
    
    const loadThumb = async () => {
      try {
        // Use optimized thumbnail endpoint for fast loading
        const response = await axios.get(`${API}/capture/${captureId}/thumbnail`);
        setThumbUrl(response.data?.thumbnail || null);
      } catch (error) {
        console.error('Error loading thumbnail:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadThumb();
  }, [captureId]);

  if (loading) {
    return (
      <div className="w-20 h-15 bg-[#1E3550] rounded flex items-center justify-center flex-shrink-0 animate-pulse">
        <RefreshCw className="w-4 h-4 text-[#5A7A9A] animate-spin" />
      </div>
    );
  }

  if (!thumbUrl) {
    return (
      <div className="w-20 h-15 bg-[#1E3550] rounded flex items-center justify-center flex-shrink-0">
        <Camera className="w-4 h-4 text-[#5A7A9A]" />
      </div>
    );
  }

  return (
    <img 
      src={thumbUrl} 
      alt="Thumbnail" 
      className="w-20 h-15 object-cover rounded flex-shrink-0"
      style={{ width: '80px', height: '60px' }}
      loading="lazy"
    />
  );
};

// Photo Viewer Modal Component - EXACT same layout as CAPTURA
// Photo LEFT (55%), Figure list RIGHT (45%)
const PhotoViewerModal = ({ capture, concept, week, onClose, isAdmin = false, onDeleteFigure }) => {
  const [photoBase64, setPhotoBase64] = useState(null);
  const [thumbBase64, setThumbBase64] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(true);

  useEffect(() => {
    if (!capture?.id) return;
    
    // First load thumbnail for instant preview
    const loadThumb = async () => {
      try {
        const response = await axios.get(`${API}/capture/${capture.id}/thumbnail`);
        setThumbBase64(response.data?.thumbnail || null);
      } catch (error) {
        console.error('Error loading thumbnail:', error);
      }
    };
    
    // Then load full photo
    const loadPhoto = async () => {
      setLoadingPhoto(true);
      try {
        const response = await axios.get(`${API}/capture/${capture.id}/photo`);
        setPhotoBase64(response.data?.photo_base64 || null);
      } catch (error) {
        console.error('Error loading photo:', error);
      } finally {
        setLoadingPhoto(false);
      }
    };
    
    loadThumb();
    loadPhoto();
  }, [capture?.id]);

  if (!capture) return null;

  const formatDimensions = (fig) => {
    const cantidad = fig.cantidad || 0;
    const cantidadFmt = cantidad < 0.01 && cantidad > 0 
      ? cantidad.toFixed(4) 
      : cantidad.toFixed(2);
    
    if (fig.largo && fig.alto && fig.profundidad) {
      return `L:${parseFloat(fig.largo).toFixed(2)}×A:${parseFloat(fig.alto).toFixed(2)}×Prof:${parseFloat(fig.profundidad).toFixed(2)}=${cantidadFmt}`;
    } else if (fig.largo && fig.alto) {
      return `L:${parseFloat(fig.largo).toFixed(2)}×A:${parseFloat(fig.alto).toFixed(2)}=${cantidadFmt}`;
    } else if (fig.longitud) {
      return `L:${parseFloat(fig.longitud).toFixed(2)}`;
    } else if (fig.piezas) {
      return `${fig.piezas} pines`;
    }
    return cantidadFmt;
  };

  // Convert figures to savedShapes format for ConstructionCanvas
  // Must include color and labelPositions exactly as saved
  const SUMA_COLOR = '#3B82F6';  // Blue - same as CAPTURA
  const DEDUCCION_COLOR = '#EF4444';  // Red - same as CAPTURA
  
  const savedShapes = capture.figures
    .filter(fig => fig.shape_data && (fig.shape_data.vertices || fig.shape_data.center || fig.shape_data.points))
    .map(fig => {
      const isDeduction = fig.tipo === 'deduccion';
      // Use saved color, or default based on deduction status
      const shapeColor = fig.shape_data?.color || fig.color || (isDeduction ? DEDUCCION_COLOR : SUMA_COLOR);
      
      // For line figures, points are stored instead of vertices
      const vertices = fig.shape_data?.vertices || fig.shape_data?.points;
      
      console.log('GENERADORES shape:', {
        id: fig.id?.substring(0, 10),
        tipo: fig.tipo,
        type: fig.shape_data?.type,
        color: shapeColor,
        shape_data_color: fig.shape_data?.color,
        fig_color: fig.color,
        hasVertices: !!fig.shape_data?.vertices,
        hasPoints: !!fig.shape_data?.points,
        labelPositions: fig.label_positions
      });
      
      return {
        id: fig.id,
        type: fig.shape_data?.type,
        vertices: vertices,  // Use vertices OR points for line figures
        center: fig.shape_data?.center,
        radius: fig.shape_data?.radius,
        isPercent: fig.shape_data?.isPercent, // Preserve percentage flag for coordinate conversion
        color: shapeColor,  // Explicit color - blue for suma, red for deduccion
        figureNumber: fig.figure_number,
        isDeduction: isDeduction,
        measurements: {
          largo: fig.largo,
          alto: fig.alto,
          profundidad: fig.profundidad,
          base: fig.base,
          altura: fig.altura,
          baseMayor: fig.baseMayor || fig.base_mayor,
          baseMenor: fig.baseMenor || fig.base_menor,
          diametro: fig.diametro,
          longitud: fig.longitud,
          unit: concept.unidad,
          shapeType: fig.shape_data?.type,
        },
        // Pass labelPositions exactly as saved - these contain {x, y, rotation} for each label
        labelPositions: fig.label_positions || fig.labelPositions || {},
      };
    });

  // Get pins from pza figures
  const pins = capture.figures
    .filter(fig => fig.pins && fig.pins.length > 0)
    .flatMap(fig => fig.pins);

  return (
    <div className="fixed inset-0 z-50 flex flex-col h-screen overflow-hidden bg-[#060C18]" data-testid="photo-viewer-modal">
      {/* Header - same style as CAPTURA TopBar */}
      <div className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-[#E2EAF4] hover:bg-[#1E3550]"
          >
            <X className="w-5 h-5" />
          </Button>
          <div>
            <h3 className="font-bold text-sm text-[#FF6B00]">{concept.clave} - {week.name}</h3>
            <p className="text-xs text-[#5A7A9A]">{concept.descripcion}</p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-xs text-[#5A7A9A]">Figuras: {capture.figures.length}</span>
        </div>
      </div>

      {/* EXACT SAME LAYOUT AS CAPTURA: capture-split */}
      <div className="capture-split flex-1">
        {/* LEFT: Canvas Panel (55%) - EXACT same as CAPTURA */}
        <div className="canvas-panel" data-testid="viewer-canvas-panel">
          {loadingPhoto && thumbBase64 ? (
            // Show blurred thumbnail while loading full photo
            <div className="relative w-full h-full">
              <img 
                src={thumbBase64} 
                alt="Loading..." 
                className="w-full h-full object-contain"
                style={{ filter: 'blur(8px)', transform: 'scale(1.05)' }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <RefreshCw className="w-8 h-8 text-white animate-spin" />
              </div>
            </div>
          ) : loadingPhoto ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-12 h-12 text-[#5A7A9A] animate-spin" />
            </div>
          ) : photoBase64 ? (
            <ConstructionCanvas
              readOnly={true}
              photoUrl={photoBase64}
              savedShapes={savedShapes}
              pins={pins}
              unit={concept.unidad}
              captureState="PHOTO_LOADED"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Camera className="w-16 h-16 text-[#1E3550]" />
            </div>
          )}
        </div>

        {/* RIGHT: Form Panel (45%) - Figure list only, no controls */}
        <div className="form-panel bg-[#0D1520]" data-testid="viewer-form-panel">
          {/* Figure list header */}
          <div className="bg-[#0A1422] -mx-3 -mt-3 px-3 py-2 mb-3 border-b border-[#1E3550]">
            <h4 className="font-semibold text-[#E2EAF4] text-sm">
              FIGURAS ({capture.figures.length})
            </h4>
          </div>

          {/* Figure list - scrollable */}
          <div className="flex-1 overflow-y-auto -mx-3 px-3">
            <table className="w-full text-xs">
              <thead className="bg-[#0A1422] sticky top-0">
                <tr>
                  <th className="py-1.5 px-2 text-left font-semibold text-[#5A7A9A]">No.</th>
                  <th className="py-1.5 px-2 text-left font-semibold text-[#5A7A9A]">Dimensiones</th>
                  <th className="py-1.5 px-2 text-right font-semibold text-[#5A7A9A]">Cantidad</th>
                  {isAdmin && <th className="py-1.5 px-1 w-8"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E3550]">
                {capture.figures.map((fig) => {
                  const isDeduccion = fig.tipo === 'deduccion';
                  const cantidad = fig.cantidad || 0;
                  const cantidadFmt = cantidad < 0.01 && cantidad > 0 
                    ? cantidad.toFixed(4) 
                    : cantidad.toFixed(2);
                  
                  return (
                    <tr key={fig.id} className={isDeduccion ? 'bg-red-500/10' : ''} data-testid={`viewer-figure-row-${fig.id}`}>
                      <td className="py-1.5 px-2 font-mono text-[#E2EAF4]">
                        {String(fig.figure_number).padStart(6, '0')}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-[#5A7A9A]">
                        {formatDimensions(fig)}
                      </td>
                      <td className={`py-1.5 px-2 font-mono font-semibold text-right ${isDeduccion ? 'text-red-500' : 'text-[#22C55E]'}`}>
                        {isDeduccion ? '-' : '+'}{cantidadFmt}{concept.unidad}
                      </td>
                      {isAdmin && (
                        <td className="py-1 px-1 text-right">
                          <button
                            type="button"
                            onClick={() => onDeleteFigure?.(fig)}
                            className="p-1 rounded text-[#5A7A9A] hover:text-red-400 hover:bg-red-500/10"
                            title="Eliminar figura"
                            data-testid={`btn-delete-figure-${fig.id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Subtotal - fixed at bottom */}
          <div className="bg-[#0066FF] -mx-3 -mb-3 mt-3 px-3 py-3">
            <div className="flex justify-between items-center">
              <span className="font-bold text-white">SUBTOTAL FOTO:</span>
              <span className="font-mono font-bold text-white text-lg">
                {capture.subtotal.toFixed(2)} {concept.unidad}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneradoresPage;
